// app.config.ts
import { ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withPreloading, PreloadAllModules, RouteReuseStrategy } from '@angular/router';
import { routes } from '../app/app.routes';
import { isPlatform, IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

// Firebase
import { initializeApp, provideFirebaseApp, getApp } from '@angular/fire/app';
import { environment } from '../environments/environment';
import {
  initializeAuth,
  indexedDBLocalPersistence,
  provideAuth,
  getAuth,
  connectAuthEmulator,
  browserLocalPersistence,
} from '@angular/fire/auth';
import { provideDatabase, getDatabase, connectDatabaseEmulator } from '@angular/fire/database';
import { provideFirestore, getFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { provideFunctions, getFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { provideMessaging, getMessaging, getToken } from '@angular/fire/messaging';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideAppCheck, initializeAppCheck, ReCaptchaV3Provider, CustomProvider } from '@angular/fire/app-check';

// Charts
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

// Interceptors
import { RateLimitInterceptor } from './interceptors/rate-limit.interceptor';

function isLocalBrowserRuntime(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function resolveBrowserDebugToken(enabledToken: string | null): string | false {
  const currentToken = (globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean })
    .FIREBASE_APPCHECK_DEBUG_TOKEN;

  // Preserve a token pre-injected by Playwright/local init script, but only for local browser runtimes.
  if (typeof currentToken === 'string' && currentToken.trim().length > 0) {
    return currentToken.trim();
  }

  if (enabledToken && enabledToken.trim().length > 0) {
    return enabledToken.trim();
  }

  return false;
}

function configureBrowserAppCheckDebugToken(enabledToken: string | null): void {
  // Debug-token mode is local browser E2E only. Manual local browsing and every deployed runtime
  // stay on the configured reCAPTCHA provider unless Playwright injects a registered token.
  const browserDebugToken =
    !environment.production && isLocalBrowserRuntime() ? resolveBrowserDebugToken(enabledToken) : false;
  (
    globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }
  ).FIREBASE_APPCHECK_DEBUG_TOKEN = browserDebugToken;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideIonicAngular(),
    importProvidersFrom(IonicStorageModule.forRoot()),

    // ✅ Firebase
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => {
      const isNative = isPlatform('capacitor') || isPlatform('hybrid');
      const auth = isNative
        ? initializeAuth(getApp(), { persistence: indexedDBLocalPersistence })
        : initializeAuth(getApp(), { persistence: browserLocalPersistence });

      if (environment.useEmulators && environment.authEmulatorHost) {
        connectAuthEmulator(auth, environment.authEmulatorHost, { disableWarnings: true });
      }

      return auth;
    }),
    provideDatabase(() => {
      const db = getDatabase();
      if (environment.useEmulators) {
        connectDatabaseEmulator(db, environment.databaseEmulatorHost, environment.databaseEmulatorPort);
      }
      return db;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.useEmulators) {
        connectFirestoreEmulator(firestore, environment.firestoreEmulatorHost, environment.firestoreEmulatorPort);
      }
      return firestore;
    }),
    provideFunctions(() => {
      const functions = getFunctions(getApp());
      if (environment.useEmulators) {
        connectFunctionsEmulator(functions, environment.functionsEmulatorHost, environment.functionsEmulatorPort);
      }
      return functions;
    }),
    provideStorage(() => getStorage()),
    provideMessaging(() => getMessaging()),
    provideAppCheck(() => {
      const app = getApp();

      // 1. Fully disabled for E2E/Emulators
      if (!environment.useAppCheck) {
        return initializeAppCheck(app, {
          provider: new CustomProvider({
            getToken: async () => ({
              token: 'app-check-disabled',
              expireTimeMillis: Date.now() + 3600000,
            }),
          }),
          isTokenAutoRefreshEnabled: false,
        });
      }

      // 2. Preserve only a registered token injected before app boot; otherwise use real App Check.
      configureBrowserAppCheckDebugToken(environment.appCheckDebugToken);

      // 3. Initialize with Production/Dev Provider
      return initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(environment.recaptcha3SiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    }),
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withInterceptorsFromDi()),
    provideCharts(withDefaultRegisterables()),

    // Ionic routing strategy
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: RateLimitInterceptor, multi: true },
  ],
};
